import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  AddOutlined,
  EditOutlined,
  FemaleOutlined,
  InfoOutlined,
  MaleOutlined,
  NotesOutlined,
  PetsOutlined,
  ScaleOutlined,
  SearchOutlined,
  SpaOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";
import "dayjs/locale/es";

import CustomSnackBar from "../../Components/CustomSnackBar";
import { hasAnyPermission, hasPermission } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { groomingService } from "../../services";

dayjs.locale("es");

const PET_SPECIES_OPTIONS = [
  { value: "DOG", label: "Perro" },
  { value: "CAT", label: "Gato" },
  { value: "OTHER", label: "Otro" },
];

const PET_SEX_OPTIONS = [
  { value: "UNKNOWN", label: "No definido" },
  { value: "MALE", label: "Macho" },
  { value: "FEMALE", label: "Hembra" },
];

const PET_SIZE_OPTIONS = [
  { value: "UNKNOWN", label: "No definido" },
  { value: "XS", label: "XS" },
  { value: "S", label: "Pequeño" },
  { value: "M", label: "Mediano" },
  { value: "L", label: "Grande" },
  { value: "XL", label: "XL" },
];

const COMMON_BREEDS = [
  "Labrador",
  "Golden Retriever",
  "Poodle",
  "Bulldog",
  "Chihuahua",
  "Shih Tzu",
  "Schnauzer",
  "Husky",
  "Beagle",
  "Pastor Alemán",
  "Pug",
  "Cocker Spaniel",
  "Dachshund",
  "Rottweiler",
  "Mestizo",
];

const buildPetForm = (pet = null) => ({
  name: String(pet?.name || ""),
  species: String(pet?.species || "DOG"),
  breed: String(pet?.breed || ""),
  sex: String(pet?.sex || "UNKNOWN"),
  color: String(pet?.color || ""),
  size_category: String(pet?.size_category || "UNKNOWN"),
  weight: pet?.weight === "0.00" || pet?.weight ? String(pet.weight) : "",
  birth_date: String(pet?.birth_date || ""),
  notes: String(pet?.notes || ""),
  is_active: pet?.is_active !== false,
});

const extractErrorMessage = (
  error,
  fallback = "No se pudo completar la operación."
) => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return fallback;
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;

  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];

  if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
  if (typeof firstValue === "string" && firstValue.trim()) return firstValue;

  return fallback;
};

const sortPets = (pets) =>
  [...(Array.isArray(pets) ? pets : [])].sort((left, right) =>
    String(left?.name || "").localeCompare(String(right?.name || ""), "es", {
      sensitivity: "base",
    })
  );

const getSpeciesLabel = (value) =>
  PET_SPECIES_OPTIONS.find((option) => option.value === value)?.label ||
  "Mascota";

const getSizeLabel = (value) =>
  PET_SIZE_OPTIONS.find((option) => option.value === value)?.label ||
  "No definido";

const getSexLabel = (value) =>
  PET_SEX_OPTIONS.find((option) => option.value === value)?.label ||
  "No definido";

const formatBirthDate = (value) => {
  if (!value) return "";
  const parsed = dayjs(value);
  if (!parsed.isValid()) return "";
  return parsed.format("DD/MM/YYYY");
};

const getAgeLabel = (value) => {
  if (!value) return "";
  const birthDate = new Date(`${value}T00:00:00`);
  if (Number.isNaN(birthDate.getTime())) return "";
  const now = new Date();
  if (birthDate.getTime() > now.getTime()) return "";
  let years = now.getFullYear() - birthDate.getFullYear();
  const monthDiff = now.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDate.getDate())) {
    years -= 1;
  }
  if (years >= 1) return `${years} ano${years === 1 ? "" : "s"}`;
  const monthApprox = Math.max(
    0,
    (now.getFullYear() - birthDate.getFullYear()) * 12 + (now.getMonth() - birthDate.getMonth())
  );
  return `${monthApprox} mes${monthApprox === 1 ? "" : "es"}`;
};

const getPetAccent = (species) => {
  switch (species) {
    case "DOG":
      return {
        main: "#02A69C",
        soft: "rgba(2, 166, 156, 0.10)",
        border: "rgba(2, 166, 156, 0.24)",
      };
    case "CAT":
      return {
        main: "#8E5CF7",
        soft: "rgba(142, 92, 247, 0.10)",
        border: "rgba(142, 92, 247, 0.24)",
      };
    default:
      return {
        main: "#64748B",
        soft: "rgba(100, 116, 139, 0.10)",
        border: "rgba(100, 116, 139, 0.22)",
      };
  }
};

const getSexIcon = (sex) => {
  if (sex === "MALE") return <MaleOutlined sx={{ fontSize: 15 }} />;
  if (sex === "FEMALE") return <FemaleOutlined sx={{ fontSize: 15 }} />;
  return null;
};

function CompactPetCard({ pet, onEdit, canEdit }) {
  const accent = getPetAccent(pet?.species);

  return (
    <Paper
      variant="outlined"
      sx={{
        position: "relative",
        overflow: "hidden",
        p: 1.35,
        borderRadius: 3,
        borderColor: accent.border,
        bgcolor: "background.paper",
        background: `linear-gradient(135deg, ${accent.soft} 0%, rgba(255,255,255,0.96) 42%, rgba(255,255,255,1) 100%)`,
        transition: "160ms ease",
        "&:hover": {
          borderColor: accent.main,
          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.10)",
          transform: "translateY(-1px)",
        },
        "&:before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: accent.main,
          opacity: 0.9,
        },
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1.15} alignItems="center">
          <Badge
            overlap="circular"
            color={pet?.is_active === false ? "default" : "success"}
            variant="dot"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Avatar
              sx={{
                width: 42,
                height: 42,
                bgcolor: accent.soft,
                color: accent.main,
                border: "1px solid",
                borderColor: accent.border,
              }}
            >
              <PetsOutlined fontSize="small" />
            </Avatar>
          </Badge>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 900,
                  lineHeight: 1.1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {pet?.name || "Mascota"}
              </Typography>

              {pet?.is_active === false ? (
                <Chip
                  size="small"
                  label="Inactiva"
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 800,
                    bgcolor: "grey.200",
                  }}
                />
              ) : null}
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 0.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {getSpeciesLabel(pet?.species)}
              {pet?.breed ? ` • ${pet.breed}` : " • Sin raza"}
            </Typography>
          </Box>

          {canEdit ? (
            <Tooltip title="Editar mascota">
              <Button
                size="small"
                onClick={() => onEdit(pet)}
                sx={{
                  minWidth: 34,
                  width: 34,
                  height: 34,
                  borderRadius: 2,
                  color: accent.main,
                  bgcolor: alpha(accent.main, 0.08),
                  "&:hover": {
                    bgcolor: alpha(accent.main, 0.14),
                  },
                }}
              >
                <EditOutlined sx={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
          ) : null}
        </Stack>

        <Stack direction="row" spacing={0.65} useFlexGap flexWrap="wrap">
          <Chip
            size="small"
            label={getSizeLabel(pet?.size_category)}
            sx={{ height: 23, fontSize: 11.5, fontWeight: 700 }}
          />

          <Chip
            size="small"
            icon={getSexIcon(pet?.sex)}
            label={getSexLabel(pet?.sex)}
            sx={{ height: 23, fontSize: 11.5, fontWeight: 700 }}
          />

          <Chip
            size="small"
            icon={<ScaleOutlined sx={{ fontSize: 14 }} />}
            label={pet?.weight ? `${pet.weight} lb` : "Sin peso"}
            sx={{ height: 23, fontSize: 11.5, fontWeight: 700 }}
          />

          {pet?.birth_date ? (
            <Chip
              size="small"
              label={`Edad: ${getAgeLabel(pet.birth_date) || "-"}`}
              sx={{ height: 23, fontSize: 11.5, fontWeight: 700 }}
            />
          ) : null}

          {pet?.color ? (
            <Chip
              size="small"
              label={pet.color}
              sx={{ height: 23, fontSize: 11.5, fontWeight: 700 }}
            />
          ) : null}
        </Stack>

        {pet?.notes ? (
          <Tooltip title={pet.notes}>
            <Box
              sx={{
                px: 1,
                py: 0.7,
                borderRadius: 2,
                bgcolor: "rgba(15, 23, 42, 0.035)",
                border: "1px solid",
                borderColor: "rgba(15, 23, 42, 0.06)",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {pet.notes}
              </Typography>
            </Box>
          </Tooltip>
        ) : null}
      </Stack>
    </Paper>
  );
}

function PetFormDialog({
  open,
  mode,
  petForm,
  setPetForm,
  onClose,
  onSave,
  saving,
}) {
  const handleFormChange = (field) => (event) => {
    const value = event?.target?.value ?? "";
    setPetForm((prev) => ({ ...prev, [field]: value }));
  };
  const todayIso = dayjs().format("YYYY-MM-DD");
  const birthDateValue = String(petForm.birth_date || "");
  const birthDateInvalid = Boolean(birthDateValue && birthDateValue > todayIso);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar
            sx={{
              bgcolor: "primary.main",
              boxShadow: "0 10px 24px rgba(2, 166, 156, 0.22)",
            }}
          >
            <PetsOutlined />
          </Avatar>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {mode === "edit" ? "Editar mascota" : "Registrar mascota"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Información útil para grooming, historial y atención.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2, bgcolor: "grey.50" }}>
        <Stack spacing={2.2}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <InfoOutlined fontSize="small" color="action" />
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Información principal
              </Typography>
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              <TextField
                label="Nombre"
                placeholder="Ej. Max"
                value={petForm.name}
                onChange={handleFormChange("name")}
                fullWidth
              />

              <TextField
                select
                label="Especie"
                value={petForm.species}
                onChange={handleFormChange("species")}
                fullWidth
              >
                {PET_SPECIES_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <Autocomplete
                freeSolo
                options={COMMON_BREEDS}
                value={petForm.breed || ""}
                onInputChange={(_, nextValue) =>
                  setPetForm((prev) => ({ ...prev, breed: String(nextValue || "") }))
                }
                onChange={(_, nextValue) =>
                  setPetForm((prev) => ({
                    ...prev,
                    breed: typeof nextValue === "string" ? nextValue : "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Raza"
                    placeholder="Ej. Labrador"
                    fullWidth
                  />
                )}
              />

              <TextField
                label="Color"
                placeholder="Ej. Blanco"
                value={petForm.color}
                onChange={handleFormChange("color")}
                fullWidth
              />
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <PetsOutlined fontSize="small" color="action" />
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Características
              </Typography>
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              <TextField
                select
                label="Sexo"
                value={petForm.sex}
                onChange={handleFormChange("sex")}
                fullWidth
              >
                {PET_SEX_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Tamaño"
                value={petForm.size_category}
                onChange={handleFormChange("size_category")}
                fullWidth
              >
                {PET_SIZE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Peso (lb)"
                placeholder="Ej. 8.5"
                value={petForm.weight}
                onChange={handleFormChange("weight")}
                fullWidth
              />

              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                <DatePicker
                  label="Fecha de nacimiento"
                  value={petForm.birth_date ? dayjs(petForm.birth_date) : null}
                  onChange={(nextValue) => {
                    const normalized = nextValue && nextValue.isValid()
                      ? nextValue.format("YYYY-MM-DD")
                      : "";
                    setPetForm((prev) => ({ ...prev, birth_date: normalized }));
                  }}
                  format="DD/MM/YYYY"
                  maxDate={dayjs().endOf("day")}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: birthDateInvalid,
                      helperText: birthDateInvalid
                        ? "La fecha no puede estar en el futuro."
                        : getAgeLabel(petForm.birth_date)
                          ? `Edad aproximada: ${getAgeLabel(petForm.birth_date)}`
                          : " ",
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            {petForm.birth_date ? (
              <Typography variant="caption" color="text.secondary">
                Nacimiento: {formatBirthDate(petForm.birth_date) || "-"}
              </Typography>
            ) : null}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <NotesOutlined fontSize="small" color="action" />
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Notas relevantes
              </Typography>
            </Stack>

            <TextField
              label="Cuidados, comportamiento, observaciones..."
              value={petForm.notes}
              onChange={handleFormChange("notes")}
              multiline
              minRows={3}
              fullWidth
            />
          </Paper>

          {mode === "edit" ? (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                Opciones internas
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(petForm.is_active)}
                    onChange={(event) =>
                      setPetForm((prev) => ({
                        ...prev,
                        is_active: event.target.checked,
                      }))
                    }
                  />
                }
                label={petForm.is_active ? "Mascota activa" : "Mascota inactiva"}
              />

              <Typography variant="caption" color="text.secondary" display="block">
                Este ajuste es interno y no forma parte de la información principal.
              </Typography>
            </Paper>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={onSave} disabled={saving}>
          {saving
            ? "Guardando..."
            : mode === "edit"
            ? "Actualizar mascota"
            : "Guardar mascota"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PetEmptyState({ canManagePets, onCreate }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 3, md: 5 },
        textAlign: "center",
        borderRadius: 4,
        borderStyle: "dashed",
        borderColor: "rgba(2, 166, 156, 0.28)",
        bgcolor: "rgba(2, 166, 156, 0.035)",
      }}
    >
      <Stack spacing={1.5} alignItems="center">
        <Avatar
          sx={{
            width: 64,
            height: 64,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            boxShadow: "0 12px 26px rgba(2, 166, 156, 0.22)",
          }}
        >
          <PetsOutlined />
        </Avatar>

        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          Este cliente aún no tiene mascotas registradas
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440 }}>
          Registra la primera mascota para usarla en servicios de grooming,
          historial y futuras reservas.
        </Typography>

        {canManagePets ? (
          <Button variant="contained" startIcon={<AddOutlined />} onClick={onCreate}>
            Agregar primera mascota
          </Button>
        ) : null}
      </Stack>
    </Paper>
  );
}

function CustomerPetsDialog({ open, onClose, customer, onPetsChange }) {
  const { currentBranch, effectivePerms = [], isSuperuser } = useAuth();

  const branchId = currentBranch?.id || null;
  const branchName = currentBranch?.alias || currentBranch?.name || "";

  const canViewPets = hasAnyPermission(
    effectivePerms,
    ["grooming_view", "grooming_manage", "grooming_convert_sale"],
    isSuperuser
  );

  const canManagePets = hasPermission(
    effectivePerms,
    "grooming_manage",
    isSuperuser
  );

  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [speciesFilter, setSpeciesFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [formDialog, setFormDialog] = useState({
    open: false,
    mode: "create",
    pet: null,
  });

  const [petForm, setPetForm] = useState(buildPetForm());
  const [saving, setSaving] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  const syncPets = useCallback(
    (nextPets) => {
      const sorted = sortPets(nextPets);
      setPets(sorted);
      onPetsChange?.(sorted);
    },
    [onPetsChange]
  );

  const loadPets = useCallback(async () => {
    if (!open || !branchId || !customer?.id || !canViewPets) {
      setPets([]);
      return;
    }

    setLoading(true);

    try {
      const data = await groomingService.listCustomerPets({
        branch_id: branchId,
        customer_id: customer.id,
      });

      syncPets(data?.pets || []);
    } catch (error) {
      setPets([]);
      showSnack(
        extractErrorMessage(
          error,
          "No se pudieron cargar las mascotas del cliente."
        ),
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [open, branchId, customer?.id, canViewPets, syncPets, showSnack]);

  useEffect(() => {
    loadPets();
  }, [loadPets]);

  useEffect(() => {
    if (!open) {
      setFormDialog({ open: false, mode: "create", pet: null });
      setPetForm(buildPetForm());
      setSaving(false);
      setSearchText("");
      setSpeciesFilter("ALL");
      setStatusFilter("ALL");
    }
  }, [open]);

  const openCreateDialog = useCallback(() => {
    setPetForm(buildPetForm());
    setFormDialog({ open: true, mode: "create", pet: null });
  }, []);

  const openEditDialog = useCallback((pet) => {
    setPetForm(buildPetForm(pet));
    setFormDialog({ open: true, mode: "edit", pet });
  }, []);

  const closeFormDialog = useCallback(() => {
    if (saving) return;
    setFormDialog({ open: false, mode: "create", pet: null });
    setPetForm(buildPetForm());
  }, [saving]);

  const handleSave = useCallback(async () => {
    if (!branchId || !customer?.id || !canManagePets) return;

    if (!String(petForm.name || "").trim()) {
      showSnack("Debes ingresar el nombre de la mascota.", "warning");
      return;
    }
    const birthDateValue = String(petForm.birth_date || "").trim();
    const todayIso = dayjs().format("YYYY-MM-DD");
    if (birthDateValue && birthDateValue > todayIso) {
      showSnack("La fecha de nacimiento no puede estar en el futuro.", "warning");
      return;
    }

    setSaving(true);

    const payload = {
      branch_id: branchId,
      customer_id: customer.id,
      name: String(petForm.name || "").trim(),
      species: petForm.species || "DOG",
      breed: String(petForm.breed || "").trim() || undefined,
      sex: petForm.sex || "UNKNOWN",
      color: String(petForm.color || "").trim() || undefined,
      size_category: petForm.size_category || "UNKNOWN",
      weight: String(petForm.weight || "").trim() || undefined,
      birth_date: birthDateValue || undefined,
      notes: String(petForm.notes || "").trim() || undefined,
      is_active: Boolean(petForm.is_active),
    };

    try {
      if (formDialog.mode === "edit" && formDialog.pet?.id) {
        const data = await groomingService.updatePet(formDialog.pet.id, payload);
        const updatedPet = data?.pet;

        syncPets(
          pets.map((pet) => (pet.id === updatedPet?.id ? updatedPet : pet))
        );

        showSnack("Mascota actualizada correctamente.", "success");
      } else {
        const data = await groomingService.createPet(payload);
        const createdPet = data?.pet;

        syncPets([...pets, createdPet].filter(Boolean));
        showSnack("Mascota creada correctamente.", "success");
      }

      closeFormDialog();
    } catch (error) {
      showSnack(
        extractErrorMessage(error, "No se pudo guardar la mascota."),
        "error"
      );
    } finally {
      setSaving(false);
    }
  }, [
    branchId,
    customer?.id,
    canManagePets,
    petForm,
    formDialog.mode,
    formDialog.pet?.id,
    pets,
    syncPets,
    showSnack,
    closeFormDialog,
  ]);

  const summaryText = useMemo(() => {
    if (!customer) return "";
    return [customer?.phone_number || "Sin teléfono", "Gestión de mascotas"]
      .filter(Boolean)
      .join(" • ");
  }, [customer]);

  const filteredPets = useMemo(() => {
    const normalizedSearch = String(searchText || "").trim().toLowerCase();

    return pets.filter((pet) => {
      const matchesSearch =
        !normalizedSearch ||
        [
          pet?.name,
          pet?.breed,
          pet?.color,
          pet?.notes,
          getSpeciesLabel(pet?.species),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch);

      const matchesSpecies =
        speciesFilter === "ALL" || pet?.species === speciesFilter;

      const matchesStatus =
        statusFilter === "ALL" ||
        (statusFilter === "ACTIVE" && pet?.is_active !== false) ||
        (statusFilter === "INACTIVE" && pet?.is_active === false);

      return matchesSearch && matchesSpecies && matchesStatus;
    });
  }, [pets, searchText, speciesFilter, statusFilter]);

  const activePets = useMemo(
    () => pets.filter((pet) => pet?.is_active !== false).length,
    [pets]
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            background:
              "linear-gradient(135deg, rgba(2,166,156,0.14), rgba(228,106,43,0.08), rgba(255,255,255,1))",
          }}
        >
          <Box sx={{ px: { xs: 2, md: 3 }, py: 2.4 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" spacing={1.6} alignItems="center">
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: "primary.main",
                    boxShadow: "0 14px 30px rgba(2, 166, 156, 0.24)",
                  }}
                >
                  <SpaOutlined />
                </Avatar>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 950,
                      letterSpacing: "-0.02em",
                      lineHeight: 1.1,
                    }}
                  >
                    {customer?.name || "Cliente"}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                    {summaryText}
                  </Typography>

                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                    <Chip
                      size="small"
                      icon={<PetsOutlined sx={{ fontSize: 15 }} />}
                      label={`${pets.length} mascota${pets.length === 1 ? "" : "s"}`}
                      sx={{ fontWeight: 800, bgcolor: "rgba(255,255,255,0.78)" }}
                    />
                    <Chip
                      size="small"
                      label={`${activePets} activa${activePets === 1 ? "" : "s"}`}
                      sx={{ fontWeight: 800, bgcolor: "rgba(255,255,255,0.78)" }}
                    />
                    {branchName ? (
                      <Chip
                        size="small"
                        label={branchName}
                        sx={{ fontWeight: 800, bgcolor: "rgba(255,255,255,0.78)" }}
                      />
                    ) : null}
                  </Stack>
                </Box>
              </Stack>

              {canManagePets ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="contained"
                    startIcon={<AddOutlined />}
                    onClick={openCreateDialog}
                    disabled={!branchId}
                    sx={{
                      borderRadius: 2.5,
                      fontWeight: 900,
                      px: 2.2,
                      boxShadow: "0 12px 26px rgba(2, 166, 156, 0.22)",
                    }}
                  >
                    Agregar mascota
                  </Button>
                </Stack>
              ) : null}
            </Stack>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 0,
            bgcolor: "grey.50",
          }}
        >
          {!branchId ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">
                Selecciona una sucursal para gestionar mascotas de Grooming.
              </Alert>
            </Box>
          ) : !canViewPets ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="warning">
                No tienes permisos para ver mascotas de Grooming en esta sucursal.
              </Alert>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  py: 1.5,
                  bgcolor: "background.paper",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.2}
                  alignItems={{ xs: "stretch", md: "center" }}
                  justifyContent="space-between"
                >
                  <TextField
                    size="small"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder="Buscar por nombre, raza, color o notas..."
                    sx={{ minWidth: { xs: "100%", md: 360 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchOutlined fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <TextField
                      select
                      size="small"
                      label="Especie"
                      value={speciesFilter}
                      onChange={(event) => setSpeciesFilter(event.target.value)}
                      sx={{ minWidth: 130 }}
                    >
                      <MenuItem value="ALL">Todas</MenuItem>
                      {PET_SPECIES_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      size="small"
                      label="Estado"
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                      sx={{ minWidth: 130 }}
                    >
                      <MenuItem value="ALL">Todos</MenuItem>
                      <MenuItem value="ACTIVE">Activas</MenuItem>
                      <MenuItem value="INACTIVE">Inactivas</MenuItem>
                    </TextField>

                    <Chip
                      label={`${filteredPets.length} de ${pets.length}`}
                      sx={{
                        height: 40,
                        px: 0.5,
                        fontWeight: 900,
                        bgcolor: "rgba(2,166,156,0.08)",
                        color: "primary.main",
                      }}
                    />
                  </Stack>
                </Stack>
              </Box>

              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  py: 2,
                  maxHeight: "58vh",
                  overflowY: "auto",
                }}
              >
                {loading ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(3, minmax(0, 1fr))",
                      },
                      gap: 1.25,
                    }}
                  >
                    {Array.from({ length: 6 }).map((_, index) => (
                      <Paper
                        key={index}
                        variant="outlined"
                        sx={{ p: 1.5, borderRadius: 3 }}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Skeleton variant="circular" width={42} height={42} />
                            <Box sx={{ flex: 1 }}>
                              <Skeleton variant="text" width="58%" height={24} />
                              <Skeleton variant="text" width="78%" height={18} />
                            </Box>
                          </Stack>
                          <Stack direction="row" spacing={0.75}>
                            <Skeleton variant="rounded" width={70} height={23} />
                            <Skeleton variant="rounded" width={80} height={23} />
                            <Skeleton variant="rounded" width={70} height={23} />
                          </Stack>
                          <Skeleton variant="rounded" height={32} />
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                ) : pets.length === 0 ? (
                  <PetEmptyState
                    canManagePets={canManagePets}
                    onCreate={openCreateDialog}
                  />
                ) : filteredPets.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 4,
                      borderRadius: 4,
                      textAlign: "center",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                      No hay mascotas con esos filtros
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Prueba cambiando la búsqueda, especie o estado.
                    </Typography>
                  </Paper>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(3, minmax(0, 1fr))",
                      },
                      gap: 1.25,
                    }}
                  >
                    {filteredPets.map((pet) => (
                      <CompactPetCard
                        key={pet.id}
                        pet={pet}
                        onEdit={openEditDialog}
                        canEdit={canManagePets}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.6, bgcolor: "background.paper" }}>
          <Button onClick={onClose} sx={{ fontWeight: 800 }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <PetFormDialog
        open={formDialog.open}
        mode={formDialog.mode}
        petForm={petForm}
        setPetForm={setPetForm}
        onClose={closeFormDialog}
        onSave={handleSave}
        saving={saving}
      />

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </>
  );
}

export default CustomerPetsDialog;
